import { make, commit } from "vuex-pathify";
import { Firebase } from "@/services/firebase";
import filterObject from "@/util/filter-object";
import sortBy from "lodash/sortBy";

// properties
const state = {
  items: {},
};

/**
 * This applys to all CRUD operations as defaults
 */
function defaults(state, rootState) {
  return {
    version: 2,
    watchers: [state._sync.userId],
    project_id: docketData.install_id,
    team_id: rootState?.user?.user?.team || "",
    modified: Firebase.firestore.Timestamp.now().toMillis(),
  };
}

/**
 * This applies to create operations
 */
function newDefaults(state, rootState) {
  return {
    ...defaults(state, rootState),
    done: false,
    assigned: "",
    status: "published",
    created_at: new Date(),
    created_by: state._sync.userId,
  };
}

/**
 * This applies to patch operations
 */
function updateDefaults(state, rootState) {
  return {
    ...defaults(state, rootState),
    updated_at: new Date(),
    updated_by: state._sync.userId,
  };
}

const mutations = {
  ...make.mutations(state),
};

const getters = {
  ...make.getters(state),
  byId(id) {
    return this.items[id];
  },

  published(state) {
    return filterObject(state.items, (task) => {
      return task.status === "published";
    });
  },

  // get all active items from a specific list
  active: (state, getters) => (list) => {
    if (list?.id) {
      return filterObject(getters.published, (task) => {
        return !task.done && task.list_id == list.id;
      });
    } else {
      return filterObject(getters.published, (task) => {
        return !task.done;
      });
    }
  },

  // get all active items from a specific list
  done: (state, getters) => (list) => {
    if (list?.id) {
      return filterObject(getters.published, (task) => {
        return task.done && task.list_id == list.id;
      });
    } else {
      return filterObject(getters.published, (task) => {
        return task.done;
      });
    }
  },

  activeOrdered: (state, getters) => (list) => {
    if (!list) {
      return [];
    }
    let order = list?.order || [];
    let byDate = sortBy(Object.values(getters.active(list) || {}), [
      "created_at",
    ]);
    return sortBy(byDate, [
      function(o) {
        if (order.indexOf(o.id) == -1) {
          return 99999999999;
        }
        return order.indexOf(o.id);
      },
    ]);
  },

  doneOrdered: (state, getters) => (list) => {
    if (!list) {
      return [];
    }
    let order = list?.order || [];
    let byDate = sortBy(Object.values(getters.done(list) || {}), [
      "created_at",
    ]);
    return sortBy(byDate, [
      function(o) {
        if (order.indexOf(o.id) == -1) {
          return 99999999999;
        }
        return order.indexOf(o.id);
      },
    ]);
  },

  lastModified(state) {
    let array = Object.values(state?.items || {});
    return (
      array.length &&
      array.reduce((max, item) => (max.votes > item.modified ? max : item))
        ?.modified
    );
  },
};

const actions = {
  /**
   * Bind tasks for realtime updates
   */
  async bindTasks(
    { dispatch, rootGetters, getters, state },
    clearModule = false
  ) {
    let where = [
      ["status", "==", "published"], // newer than cache // not trashed
      ["list_id", "==", rootGetters["lists/currentId"]], // current list only
    ];

    if (!rootGetters["ui/showCompleted"]) {
      where.push(["done", "==", false]);
    }

    let response = await dispatch("openDBChannel", {
      clearModule,
      clauses: {
        where,
        clauses: { limit: 100 },
      },
    });

    return response;
  },

  async bindDone({ dispatch, rootGetters, getters, state }, list_id) {
    let where = [
      ["status", "==", "published"], // newer than cache // not trashed
      ["list_id", "==", list_id], // current list only
    ];

    let response = await dispatch("openDBChannel", {
      clearModule: true,
      clauses: {
        where,
        clauses: { limit: 100 },
      },
    });

    return response;
  },

  async bindTrashed({ dispatch, rootGetters, getters, state }, list = null) {
    if (!list || !list.id) {
      console.warn("no list id provided");
      return;
    }

    let where = [
      ["status", "==", "trashed"], // newer than cache // not trashed
      ["list_id", "==", list.id], // current list only
      [
        "modified",
        ">",
        Firebase.firestore.Timestamp.now().toMillis() - 2592000000, // only newer than 30 days ago
      ],
    ];

    await dispatch("openDBChannel", {
      clearModule: true,
      clauses: {
        where,
        clauses: { limit: 100 },
      },
    });
  },

  /**
   * Use batch process to increment counters
   */
  increments(
    { rootGetters, commit },
    { list_id, value = 1, type = "total", batch }
  ) {
    // update count in list
    batch.update(rootGetters["lists/dbRef"].doc(list_id), {
      [`counts.${type}`]: Firebase.firestore.FieldValue.increment(value),
    });
    commit(
      "lists/PATCH_DOC",
      {
        id: list_id,
        counts: {
          [type]: rootGetters["lists/items"]?.[list_id]?.counts?.[type] + value,
        },
      },
      { root: true }
    );

    // update count in project
    batch.update(rootGetters["project/dbRef"], {
      [`counts.${type}`]: Firebase.firestore.FieldValue.increment(value),
    });
    commit(
      "project/PATCH_DOC",
      {
        counts: {
          [type]: rootGetters["project/project"]?.counts?.[type] + value,
        },
      },
      { root: true }
    );

    // update count in team
    batch.update(rootGetters["team/dbRef"], {
      [`counts.${type}`]: Firebase.firestore.FieldValue.increment(value),
    });
    commit(
      "team/PATCH_DOC",
      {
        counts: {
          [type]: rootGetters["team/team"]?.counts?.[type] + value,
        },
      },
      { root: true }
    );

    return batch;
  },

  /**
   * Add a task
   */
  async addTask(
    { dispatch, rootGetters, getters, commit, state, rootState },
    { data, batch = null, shouldCommit = true }
  ) {
    // list_id required
    if (!data?.list_id) {
      throw new Error("You must provide a list_id with the task.");
    }

    // maybe start batch if not provided
    if (!batch) {
      let db = Firebase.firestore();
      batch = db.batch();
    }

    let doc = getters["dbRef"].doc();
    let insert = {
      list_id: data.list_id,
      ...newDefaults(state, rootState),
      ...data,
    };

    // set new task in batch
    batch.set(doc, insert);
    commit("INSERT_DOC", {
      id: doc.id,
      ...insert,
    });

    // get list id
    batch = await dispatch("increments", {
      batch,
      list_id: data.list_id,
      value: 1,
      type: "total",
    });

    if (shouldCommit) {
      return await batch.commit();
    } else {
      return batch;
    }
  },

  async editTask({ dispatch, state, rootState }, { task, data }) {
    await dispatch("patch", {
      ...updateDefaults(state, rootState),
      id: task.id,
      ...data,
    });
  },

  async scheduleTask({ dispatch }, { task, data }) {
    dispatch("editTask", {
      task,
      data: {
        assigned: task.assigned || "", // make sure we have an assigned default
        ...data,
      },
    });
  },

  async toggleDone({ dispatch, getters, state, rootState, commit }, task) {
    let done = !task.done;

    let db = Firebase.firestore();
    let batch = db.batch();

    let insert = {
      ...updateDefaults(state, rootState),
      done,
    };

    commit("PATCH_DOC", {
      id: task.id,
      ...insert,
    });

    // then get doc by ID?
    batch.update(getters["dbRef"].doc(task.id), insert);

    batch = await dispatch("increments", {
      batch,
      list_id: task.list_id,
      value: done ? 1 : -1,
      type: "done",
    });

    return await batch.commit();
  },

  // we move the task to the trashed collection
  async removeTask({ dispatch, getters, commit, state, rootState }, task) {
    let db = Firebase.firestore();
    let batch = db.batch();

    if (task.status == "trashed") {
      console.warn("Task already trashed");
      return;
    }

    let insert = {
      ...updateDefaults(state, rootState),
      status: "trashed",
    };

    batch.update(getters["dbRef"].doc(task.id), insert);
    commit("PATCH_DOC", {
      id: task.id,
      ...insert,
    });

    batch = await dispatch("increments", {
      batch,
      list_id: task.list_id,
      value: -1,
    });

    if (task.done) {
      batch = await dispatch("increments", {
        batch,
        list_id: task.list_id,
        value: -1,
        type: "done",
      });
    }

    return await batch.commit();
  },

  async rePublish({ dispatch, getters, commit, state, rootState }, task) {
    let db = Firebase.firestore();
    let batch = db.batch();

    if (task.status == "published") {
      console.warn("Task already published");
      return;
    }

    let insert = {
      ...updateDefaults(state, rootState),
      status: "published",
    };

    batch.update(getters["dbRef"].doc(task.id), insert);
    commit("PATCH_DOC", {
      id: task.id,
      ...insert,
    });

    batch = await dispatch("increments", {
      batch,
      list_id: task.list_id,
      value: 1,
    });

    if (task.done) {
      batch = await dispatch("increments", {
        batch,
        list_id: task.list_id,
        value: 1,
        type: "done",
      });
    }

    return await batch.commit();
  },
};

export default {
  firestorePath: "teams/{teamId}/teamTasks",
  firestoreRefType: "collection",
  moduleName: "tasks",
  statePropName: "items",
  sync: {
    debounceTimerMs: window.Cypress ? 1 : 1000,
    defaultValues: {
      done: false,
      status: "published",
      version: 2,
      modified: Firebase.firestore.Timestamp.now().toMillis(), // need updated at by default
    },
    insertHook: function(updateStore, doc, store) {
      doc.modified = Firebase.firestore.Timestamp.now().toMillis();
      doc.project_id = docketData.install_id;
      return updateStore(doc);
    },
    patchHook: function(updateStore, doc, store) {
      doc.modified = Firebase.firestore.Timestamp.now().toMillis();
      doc.project_id = docketData.install_id;
      return updateStore(doc);
    },
  },
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
